import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { analyticsApi, jobsApi, candidatesApi } from '../../lib/api';
import { hasScore } from '../../lib/score';
import { Button } from '../../components/ui/button';
import PipelineHealthSection from '../../components/dashboard/PipelineHealthSection';
import {
    Briefcase, Users, TrendingUp, Plus, ArrowRight, FileText,
    Target, AlertCircle, CheckCircle, Clock
} from 'lucide-react';

export default function DashboardHome() {
    const { t } = useTranslation();
    const [analytics, setAnalytics] = useState(null);
    const [recentJobs, setRecentJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [analyticsRes, jobsRes] = await Promise.all([
                analyticsApi.getDashboard(),
                jobsApi.getAll()
            ]);
            setAnalytics(analyticsRes.data);
            setRecentJobs((jobsRes.data.jobs || jobsRes.data).slice(0, 5));
        } catch (error) {
            console.error('Failed to load dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const scoreColors = {
        excellent: '#10B981',
        good: '#3B82F6',
        average: '#F59E0B',
        poor: '#EF4444'
    };

    const getScoreColor = (score) => {
        if (score >= 80) return scoreColors.excellent;
        if (score >= 60) return scoreColors.good;
        if (score >= 40) return scoreColors.average;
        return scoreColors.poor;
    };

    const scoreRanges = [
        { boundary: 0, range: '0-20', fill: scoreColors.poor },
        { boundary: 20, range: '20-40', fill: scoreColors.poor },
        { boundary: 40, range: '40-60', fill: scoreColors.average },
        { boundary: 60, range: '60-80', fill: scoreColors.good },
        { boundary: 80, range: '80-100', fill: scoreColors.excellent },
    ];

    const rawDistribution = analytics?.score_distribution || [];
    const distributionMap = {};
    rawDistribution.forEach(item => { distributionMap[item._id] = item.count; });

    const scoreDistributionData = scoreRanges.map(r => ({
        range: r.range,
        count: distributionMap[r.boundary] || 0,
        fill: r.fill
    }));

    const totalScored = scoreDistributionData.reduce((sum, item) => sum + item.count, 0);
    const scoreDistributionWithPct = scoreDistributionData.map(item => ({
        ...item,
        pct: totalScored > 0 ? Math.round((item.count / totalScored) * 100) : 0
    }));

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="h-32 bg-white rounded-lg border border-[#E5E7EB] skeleton" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="dashboard-home">
            {/* Page header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit']">{t('nav.dashboard')}</h1>
                    <p className="text-[#4B5563]">{t('dashboard.overview')}</p>
                </div>
                <div className="flex gap-3">
                    <Link to="/dashboard/jobs/new">
                        <Button className="bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="new-job-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            {t('dashboard.new_job')}
                        </Button>
                    </Link>
                </div>
            </div>

            {/* Stats cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]" data-testid="stat-jobs">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-[#9CA3AF] text-sm">{t('dashboard.active_jobs')}</p>
                            <p className="text-3xl font-bold text-[#0A0A0A] font-['Outfit'] mt-1">
                                {analytics?.jobs?.active || 0}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-[#DBEAFE] rounded-lg flex items-center justify-center">
                            <Briefcase className="w-6 h-6 text-[#002FA7]" />
                        </div>
                    </div>
                    <p className="text-sm text-[#4B5563] mt-4">
                        {analytics?.jobs?.total || 0} {t('dashboard.total')}
                    </p>
                </div>

                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]" data-testid="stat-candidates">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-[#9CA3AF] text-sm">{t('dashboard.analyzed_candidates')}</p>
                            <p className="text-3xl font-bold text-[#0A0A0A] font-['Outfit'] mt-1">
                                {analytics?.candidates?.analyzed || 0}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-[#D1FAE5] rounded-lg flex items-center justify-center">
                            <Users className="w-6 h-6 text-[#10B981]" />
                        </div>
                    </div>
                    <p className="text-sm text-[#4B5563] mt-4">
                        {analytics?.candidates?.total || 0} {t('dashboard.total')}
                    </p>
                </div>

                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]" data-testid="stat-top-score">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-[#9CA3AF] text-sm">{t('dashboard.best_score')}</p>
                            <p className="text-3xl font-bold text-[#0A0A0A] font-['Outfit'] mt-1">
                                {hasScore(analytics?.top_candidates?.[0]?.score?.overall) ? analytics.top_candidates[0].score.overall : '—'}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-[#FEF3C7] rounded-lg flex items-center justify-center">
                            <Target className="w-6 h-6 text-[#F59E0B]" />
                        </div>
                    </div>
                    <p className="text-sm text-[#4B5563] mt-4">
                        {t('dashboard.highest_score')}
                    </p>
                </div>

                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]" data-testid="stat-pending">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-[#9CA3AF] text-sm">{t('dashboard.pending_analysis')}</p>
                            <p className="text-3xl font-bold text-[#0A0A0A] font-['Outfit'] mt-1">
                                {(analytics?.candidates?.total || 0) - (analytics?.candidates?.analyzed || 0)}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-[#FEE2E2] rounded-lg flex items-center justify-center">
                            <Clock className="w-6 h-6 text-[#EF4444]" />
                        </div>
                    </div>
                    <p className="text-sm text-[#4B5563] mt-4">
                        {t('dashboard.to_analyze')}
                    </p>
                </div>
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Score distribution */}
                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]" data-testid="score-distribution">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">
                            {t('dashboard.score_distribution')}
                        </h2>
                        {totalScored > 0 && (
                            <span className="text-sm text-[#9CA3AF]">{totalScored === 1 ? t('dashboard.candidates_scored', { count: totalScored }) : t('dashboard.candidates_scored_plural', { count: totalScored })}</span>
                        )}
                    </div>
                    
                    {scoreDistributionWithPct.length > 0 && totalScored > 0 ? (
                        <div className="space-y-3 mt-4">
                            {scoreDistributionWithPct.map((item, index) => {
                                const labels = [t('dashboard.score_label_weak'), t('dashboard.score_label_insufficient'), t('dashboard.score_label_average'), t('dashboard.score_label_good'), t('dashboard.score_label_excellent')];
                                const label = labels[index] || item.range;
                                const maxCount = Math.max(...scoreDistributionWithPct.map(i => i.count), 1);
                                const barWidth = Math.max((item.count / maxCount) * 100, item.count > 0 ? 6 : 0);
                                
                                return (
                                    <div key={item.range} className="group" data-testid={`score-range-${item.range}`}>
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-medium text-[#4B5563] w-16">{item.range}</span>
                                                <span className="text-xs text-[#9CA3AF]">{label}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-bold" style={{ color: item.fill }}>{item.count}</span>
                                                <span className="text-xs text-[#9CA3AF] w-10 text-right">{item.pct}%</span>
                                            </div>
                                        </div>
                                        <div className="w-full h-6 bg-[#F3F4F6] rounded overflow-hidden">
                                            <div
                                                className="h-full rounded transition-all duration-500 group-hover:opacity-80"
                                                style={{ 
                                                    width: `${barWidth}%`, 
                                                    backgroundColor: item.fill,
                                                    minWidth: item.count > 0 ? '24px' : '0'
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                            
                            {/* Summary highlight */}
                            <div className="flex gap-3 mt-4 pt-4 border-t border-[#E5E7EB]">
                                <div className="flex-1 text-center p-3 bg-[#FEE2E2] rounded-lg">
                                    <p className="text-lg font-bold text-[#DC2626]">
                                        {scoreDistributionWithPct.slice(0, 2).reduce((s, i) => s + i.count, 0)}
                                    </p>
                                    <p className="text-[10px] text-[#DC2626] font-medium">{t('dashboard.summary_weak')}</p>
                                </div>
                                <div className="flex-1 text-center p-3 bg-[#FEF3C7] rounded-lg">
                                    <p className="text-lg font-bold text-[#D97706]">
                                        {scoreDistributionWithPct[2]?.count || 0}
                                    </p>
                                    <p className="text-[10px] text-[#D97706] font-medium">{t('dashboard.summary_average')}</p>
                                </div>
                                <div className="flex-1 text-center p-3 bg-[#D1FAE5] rounded-lg">
                                    <p className="text-lg font-bold text-[#059669]">
                                        {scoreDistributionWithPct.slice(3).reduce((s, i) => s + i.count, 0)}
                                    </p>
                                    <p className="text-[10px] text-[#059669] font-medium">{t('dashboard.summary_top')}</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="h-48 flex items-center justify-center text-[#9CA3AF]">
                            <div className="text-center">
                                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                <p>{t('dashboard.no_data')}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Top candidates */}
                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">
                            {t('dashboard.top_candidates')}
                        </h2>
                        <Link to="/dashboard/candidates?top=true" className="text-[#002FA7] text-sm hover:underline flex items-center gap-1">
                            {t('dashboard.view_all_tops')}
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="space-y-4">
                        {analytics?.top_candidates?.length > 0 ? (
                            analytics.top_candidates.slice(0, 5).map((candidate, index) => (
                                <div key={candidate.id} className="flex items-center gap-4 p-3 rounded-lg hover:bg-[#F9FAFB] transition-colors">
                                    <div className="w-8 h-8 rounded-full bg-[#002FA7] flex items-center justify-center text-white font-medium text-sm">
                                        {index + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-[#0A0A0A] truncate">{candidate.name}</p>
                                        <p className="text-sm text-[#9CA3AF] truncate">
                                            {candidate.skills?.slice(0, 3).join(', ') || t('dashboard.no_skills')}
                                        </p>
                                    </div>
                                    <div 
                                        className="px-3 py-1 rounded-full text-sm font-medium"
                                        style={{ 
                                            backgroundColor: hasScore(candidate.score?.overall) ? `${getScoreColor(candidate.score.overall)}20` : '#F3F4F6',
                                            color: hasScore(candidate.score?.overall) ? getScoreColor(candidate.score.overall) : '#9CA3AF'
                                        }}
                                    >
                                        <span data-testid={`top-candidate-score-${candidate.id}`}>{hasScore(candidate.score?.overall) ? candidate.score.overall : '—'}</span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 text-[#9CA3AF]">
                                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                <p>{t('dashboard.no_analyzed_candidates')}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Pipeline health analytics */}
            <PipelineHealthSection />

            {/* Recent jobs and activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent jobs */}
                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">
                            {t('dashboard.recent_jobs')}
                        </h2>
                        <Link to="/dashboard/jobs" className="text-[#002FA7] text-sm hover:underline flex items-center gap-1">
                            {t('dashboard.view_all_jobs')}
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="space-y-3">
                        {recentJobs.length > 0 ? (
                            recentJobs.map((job) => (
                                <Link 
                                    key={job.id} 
                                    to={`/dashboard/jobs/${job.id}`}
                                    className="block p-4 rounded-lg border border-[#E5E7EB] hover:border-[#002FA7]/30 hover:shadow-sm transition-all"
                                >
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <h3 className="font-medium text-[#0A0A0A]">{job.title}</h3>
                                            <p className="text-sm text-[#9CA3AF] mt-1">
                                                {job.location || t('common.not_specified')} • {job.employment_type}
                                            </p>
                                        </div>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            job.status === 'active' ? 'bg-[#D1FAE5] text-[#059669]' :
                                            job.status === 'paused' ? 'bg-[#FEF3C7] text-[#D97706]' :
                                            'bg-[#F3F4F6] text-[#6B7280]'
                                        }`}>
                                            {job.status === 'active' ? t('dashboard.job_status_active') : job.status === 'paused' ? t('dashboard.job_status_paused') : t('dashboard.job_status_closed')}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-4 mt-3 text-sm text-[#4B5563]">
                                        <span className="flex items-center gap-1">
                                            <Users className="w-4 h-4" />
                                            {t('dashboard.candidates_count_label', { count: job.candidates_count || 0 })}
                                        </span>
                                    </div>
                                </Link>
                            ))
                        ) : (
                            <div className="text-center py-8 text-[#9CA3AF]">
                                <Briefcase className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                <p>{t('dashboard.no_jobs_created')}</p>
                                <Link to="/dashboard/jobs/new">
                                    <Button variant="outline" size="sm" className="mt-3">
                                        {t('dashboard.create_job')}
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>

                {/* Recent activity */}
                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                    <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-6">
                        {t('dashboard.recent_activity')}
                    </h2>
                    <div className="space-y-4">
                        {analytics?.recent_activity?.length > 0 ? (
                            analytics.recent_activity.map((activity, index) => (
                                <div key={index} className="flex items-start gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                        activity.status === 'analyzed' ? 'bg-[#D1FAE5]' :
                                        activity.status === 'pending' ? 'bg-[#FEF3C7]' :
                                        'bg-[#FEE2E2]'
                                    }`}>
                                        {activity.status === 'analyzed' ? (
                                            <CheckCircle className="w-4 h-4 text-[#10B981]" />
                                        ) : activity.status === 'pending' ? (
                                            <Clock className="w-4 h-4 text-[#F59E0B]" />
                                        ) : (
                                            <AlertCircle className="w-4 h-4 text-[#EF4444]" />
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[#0A0A0A] font-medium">{activity.name}</p>
                                        <p className="text-sm text-[#9CA3AF]">
                                            {activity.status === 'analyzed' ? t('dashboard.analyzed') :
                                             activity.status === 'pending' ? t('common.pending') : t('dashboard.failed')}
                                            {hasScore(activity.score?.overall) && ` · ${t('dashboard.score')}: ${activity.score.overall}`}
                                            {activity.organization_name && (
                                                <span className="ml-1"> · {activity.organization_name}</span>
                                            )}
                                        </p>
                                    </div>
                                    <span className="text-xs text-[#9CA3AF]">
                                        {new Date(activity.created_at).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'fr-FR')}
                                    </span>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 text-[#9CA3AF]">
                                <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                <p>{t('dashboard.no_recent_activity')}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
